import { Badge } from 'shared/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Label } from 'shared/ui/label'
import { RadioGroup, RadioGroupItem } from 'shared/ui/radio-group'
import { buildRiskAssessmentPayload } from 'modules/permit/utils/permitHelpers'
import { cn } from 'shared/ui/utils'
import { buildRiskValidationKey } from 'modules/permit/pages/permit-request/utils/validationTargets'

interface RiskAssessmentSectionProps {
  riskData: {
    consequence: {
      personnel: string
      equipment: string
      environment: string
      property: string
    }
    likelihood: {
      conditions: string
      accident: string
    }
  }
  setRiskData: (data: any) => void
  getValidationContainerClass?: (key?: string | null) => string
}

const LEVEL_OPTIONS = [
  { value: '1', label: 'ระดับ 1' },
  { value: '2', label: 'ระดับ 2' },
  { value: '3', label: 'ระดับ 3' },
  { value: '4', label: 'ระดับ 4' },
]

type ConsequenceKey = keyof RiskAssessmentSectionProps['riskData']['consequence']
type LikelihoodKey = keyof RiskAssessmentSectionProps['riskData']['likelihood']

const CONSEQUENCE_ROWS: { key: ConsequenceKey; label: string }[] = [
  { key: 'personnel', label: 'ด้านบุคคล' },
  { key: 'equipment', label: 'ด้านชุมชน' },
  { key: 'environment', label: 'ด้านสิ่งแวดล้อม' },
  { key: 'property', label: 'ด้านทรัพย์สิน' },
]

const LIKELIHOOD_ROWS: { key: LikelihoodKey; label: string }[] = [
  { key: 'conditions', label: 'เชิงปริมาณ' },
  { key: 'accident', label: 'เชิงคุณภาพ' },
]

const getRiskBadgeClass = (riskLevel: string) => {
  switch (riskLevel) {
    case 'Low Risk':
      return 'bg-cyan-100 text-cyan-800 border-cyan-200'
    case 'Acceptable Risk':
      return 'bg-green-100 text-green-800 border-green-200'
    case 'High Risk':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'Extreme Risk':
      return 'bg-red-100 text-red-800 border-red-200'
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200'
  }
}

export function RiskAssessmentSection({
  riskData,
  setRiskData,
  getValidationContainerClass,
}: RiskAssessmentSectionProps) {
  const handleConsequenceChange = (category: ConsequenceKey, value: string) => {
    setRiskData((prevRiskData: RiskAssessmentSectionProps['riskData']) => ({
      ...prevRiskData,
      consequence: {
        ...prevRiskData.consequence,
        [category]: value,
      },
    }))
  }

  const handleLikelihoodChange = (category: LikelihoodKey, value: string) => {
    setRiskData((prevRiskData: RiskAssessmentSectionProps['riskData']) => ({
      ...prevRiskData,
      likelihood: {
        ...prevRiskData.likelihood,
        [category]: value,
      },
    }))
  }

  const riskAssessment = buildRiskAssessmentPayload(riskData)
  const riskScore = riskAssessment?.riskScore ?? 0
  const riskLevel = riskAssessment?.riskLevel ?? 'Not Assessed'
  const riskBadgeClass = getRiskBadgeClass(riskLevel)

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">การประเมินความเสี่ยง</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
          <h4 className="mb-3 rounded bg-muted p-2 text-center font-medium">การประเมินความเสี่ยง</h4>
          <img
            src="/images/permit/RiskAssessment.png"
            alt="Risk assessment table"
            className="h-auto w-full rounded-md border border-gray-200"
          />
        </div>

        <div className="rounded-lg bg-gray-50 p-4">
          <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
            <div className="bg-sky-500 px-3 py-2 text-sm font-semibold text-white">
              ประเมินผลกระทบ (Consequence)
            </div>
            <div className="min-w-[520px] divide-y divide-gray-200">
              {CONSEQUENCE_ROWS.map((row) => (
                <div
                  key={row.key}
                  className={cn(
                    'grid grid-cols-[160px_1fr] items-center rounded-lg',
                    getValidationContainerClass?.(buildRiskValidationKey(row.key)),
                  )}
                  data-validation-key={buildRiskValidationKey(row.key)}
                >
                  <div className="px-3 py-2 text-sm font-medium text-gray-700">{row.label}</div>
                  <RadioGroup
                    value={riskData.consequence[row.key]}
                    onValueChange={(value) => handleConsequenceChange(row.key, value)}
                    className="grid grid-cols-2 gap-2 px-3 py-2 sm:grid-cols-4"
                  >
                    {LEVEL_OPTIONS.map((option) => {
                      const id = `${row.key}-${option.value}`
                      return (
                        <div key={id} className="flex items-center gap-2">
                          <RadioGroupItem value={option.value} id={id} />
                          <Label htmlFor={id} className="text-sm">
                            {option.label}
                          </Label>
                        </div>
                      )
                    })}
                  </RadioGroup>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-gray-50 p-4">
          <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
            <div className="bg-sky-500 px-3 py-2 text-sm font-semibold text-white">
              ประเมินโอกาส (Likelihood)
            </div>
            <div className="min-w-[520px] divide-y divide-gray-200">
              {LIKELIHOOD_ROWS.map((row) => (
                <div
                  key={row.key}
                  className={cn(
                    'grid grid-cols-[160px_1fr] items-center rounded-lg',
                    getValidationContainerClass?.(buildRiskValidationKey(row.key)),
                  )}
                  data-validation-key={buildRiskValidationKey(row.key)}
                >
                  <div className="px-3 py-2 text-sm font-medium text-gray-700">{row.label}</div>
                  <RadioGroup
                    value={riskData.likelihood[row.key]}
                    onValueChange={(value) => handleLikelihoodChange(row.key, value)}
                    className="grid grid-cols-2 gap-2 px-3 py-2 sm:grid-cols-4"
                  >
                    {LEVEL_OPTIONS.map((option) => {
                      const id = `${row.key}-${option.value}`
                      return (
                        <div key={id} className="flex items-center gap-2">
                          <RadioGroupItem value={option.value} id={id} />
                          <Label htmlFor={id} className="text-sm">
                            {option.label}
                          </Label>
                        </div>
                      )
                    })}
                  </RadioGroup>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-lg bg-gray-50 p-4">
          <h4 className="mb-4 rounded bg-muted p-2 text-center font-medium">
            ระดับความเสี่ยง (Risk Assessment Score)
          </h4>

          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2">
              <div className="rounded bg-primary px-4 py-2 font-medium text-primary-foreground">
                {riskAssessment ? riskScore : '-'}
              </div>
              <Badge variant="outline" className={`${riskBadgeClass} border px-4 py-2`}>
                {riskLevel}
              </Badge>
            </div>
          </div>

          {riskScore >= 6 ? (
            <div className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 p-3">
              <p className="text-sm text-yellow-800">
                งานนี้มีความเสี่ยงสูง ต้องมีมาตรการควบคุมเพิ่มเติมก่อนดำเนินการ
              </p>
              {riskScore === 16 ? (
                <p className="mt-1 text-sm text-red-800">
                  <strong>Extreme Risk:</strong> ต้องหยุดงานทันทีและทบทวนกระบวนการทำงาน
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
